import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BarChart2, LogIn, LogOut, AlertTriangle, RefreshCw, HelpCircle } from 'lucide-react';

export default function Header({ setShowTour, theme, toggleTheme }) {
  const { user, login, logout, jiraTokenWarning, jiraTokenExpired, jiraTokenMinutesLeft, authError } = useAuth();
  const location = useLocation();

  const isActive = (path) =>
    location.pathname === path ? 'nav-link active' : 'nav-link';

  return (
    <>
      <header className="header">
        <div className="header-inner">
          <Link to="/" className="header-logo">
            🃏 Planning<span>Poker</span>
          </Link>

          <nav className="header-nav">
            <Link to="/" className={isActive('/')}>Sessions</Link>

            {user && (
              <Link to="/reports" className={isActive('/reports')}>
                <span className="flex items-center gap-2">
                  <BarChart2 size={14} /> Reports
                </span>
              </Link>
            )}

            {setShowTour && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowTour(true)}
                title="How to use Planning Poker"
                style={{ color: 'var(--text-muted)' }}
              >
                <HelpCircle size={16} />
              </button>
            )}

            <button
              className="btn btn-ghost btn-sm"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{ color: 'var(--text-muted)', fontSize: 16, lineHeight: 1, padding: '4px 8px' }}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>

            <div style={{ width: 1, height: 24, background: 'var(--border)', margin: '0 8px' }} />

            {user ? (
              <div className="flex items-center gap-3">
                {user.avatarUrl && (
                  <img
                    src={user.avatarUrl}
                    alt={user.displayName}
                    style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid var(--accent)' }}
                  />
                )}
                <span className="text-sm text-muted">{user.displayName}</span>
                <button className="btn btn-ghost btn-sm" onClick={logout}>
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={login}>
                <LogIn size={14} /> Sign in with Jira
              </button>
            )}
          </nav>
        </div>
      </header>

      {authError && (
        <div style={{
          background: 'var(--red-dim)',
          borderBottom: '1px solid var(--red)',
          padding: '10px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12
        }}>
          <AlertTriangle size={14} style={{ color: 'var(--red)', flexShrink: 0 }} />
          <span className="text-sm" style={{ color: 'var(--red)' }}>
            <strong>Access denied.</strong> {authError}
          </span>
        </div>
      )}

      {/* Jira token expiry warning banner — shown below header */}
      {jiraTokenExpired && (
        <div style={{
          background: 'var(--red-dim)',
          borderBottom: '1px solid var(--red)',
          padding: '10px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12
        }}>
          <AlertTriangle size={14} style={{ color: 'var(--red)', flexShrink: 0 }} />
          <span className="text-sm" style={{ color: 'var(--red)' }}>
            <strong>Your Jira session has expired.</strong> Jira searches and story point saves will fail until you sign back in.
          </span>
          <button className="btn btn-danger btn-sm" onClick={logout} style={{ flexShrink: 0 }}>
            <RefreshCw size={12} /> Sign in again
          </button>
        </div>
      )}

      {jiraTokenWarning && !jiraTokenExpired && (
        <div style={{
          background: 'var(--yellow-dim)',
          borderBottom: '1px solid var(--yellow)',
          padding: '8px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12
        }}>
          <AlertTriangle size={14} style={{ color: 'var(--yellow)', flexShrink: 0 }} />
          <span className="text-sm" style={{ color: 'var(--yellow)' }}>
            <strong>Jira session expires in {jiraTokenMinutesLeft} minute{jiraTokenMinutesLeft !== 1 ? 's' : ''}.</strong> Finish your current round then sign out and back in.
          </span>
          <button className="btn btn-sm" style={{ background: 'var(--yellow-dim)', color: 'var(--yellow)', border: '1px solid var(--yellow)', flexShrink: 0 }} onClick={logout}>
            <RefreshCw size={12} /> Re-authenticate now
          </button>
        </div>
      )}
    </>
  );
}
